import { type RefObject, useEffect, useRef, useState } from "react";

import { type Block, type Gap, drawnBlocks, gapAt } from "./insert_gap";

// How long the + stays once the pointer leaves the draft, so he can reach it.
const LINGER_MS = 400;

export type WindowGap = Gap & { frameTop: number; frameLeft: number };

// Insert here: the gap the pointer is over in the drawn draft, while
// `enabled`. Read on a pointer move at most once a frame; gone when the draft
// scrolls. `drawn` changes whenever the draft's blocks do (a redraw, a fill).
export const useInsertGap = ({
  frameRef,
  root,
  drawn,
  enabled,
}: {
  frameRef: RefObject<HTMLIFrameElement | null>;
  root: Element | null;
  drawn: unknown;
  enabled: boolean;
}) => {
  const [gap, setGap] = useState<WindowGap | null>(null);
  const blocks = useRef<Block[]>([]);
  const hovered = useRef(false);

  useEffect(() => {
    blocks.current = root ? drawnBlocks(root) : [];
  }, [root, drawn]);

  useEffect(() => {
    const frame = frameRef.current;
    const doc = frame?.contentDocument;
    const view = frame?.contentWindow;
    if (!enabled || !root || !frame || !doc || !view) {
      setGap(null);
      return;
    }

    let pending = 0;
    let linger = 0;
    let last: MouseEvent | null = null;
    const show = () => {
      pending = 0;
      const target = last?.target as Element | null | undefined;
      if (!last || target?.nodeType !== Node.ELEMENT_NODE) return;
      const found = gapAt(blocks.current, target, last.clientY);
      const { top, left } = frame.getBoundingClientRect();
      setGap(found && { ...found, frameTop: top, frameLeft: left });
    };
    const move = (event: MouseEvent) => {
      last = event;
      window.clearTimeout(linger);
      if (!pending) pending = view.requestAnimationFrame(show);
    };
    const hide = () => setGap(null);
    let hiding = 0;
    const scroll = () => {
      if (!hiding)
        hiding = view.requestAnimationFrame(() => {
          hiding = 0;
          hide();
        });
    };
    const leave = () => {
      linger = window.setTimeout(() => {
        if (!hovered.current) hide();
      }, LINGER_MS);
    };

    doc.addEventListener("mousemove", move);
    doc.documentElement.addEventListener("mouseleave", leave);
    view.addEventListener("scroll", scroll, { passive: true });
    return () => {
      view.cancelAnimationFrame(pending);
      view.cancelAnimationFrame(hiding);
      window.clearTimeout(linger);
      doc.removeEventListener("mousemove", move);
      doc.documentElement.removeEventListener("mouseleave", leave);
      view.removeEventListener("scroll", scroll);
    };
  }, [frameRef, root, enabled]);

  const onHover = (over: boolean) => {
    hovered.current = over;
    if (!over) setGap(null);
  };

  return { gap, onHover } as const;
};
